/**
 * Stepper — −/+ stepper shell shared by the feed-level, listening-goal and
 * activity-count selectors. Arrows are 32px circular bordered buttons,
 * disabled (opacity 0.4) at the ends. Web port of the iOS selector chrome.
 */
import { FC, ReactNode } from "react";
import { Minus, Plus } from "lucide-react";
import { colors } from "@/ui";

interface StepperProps {
  canDecrease: boolean;
  canIncrease: boolean;
  onDecrease: () => void;
  onIncrease: () => void;
  children: ReactNode;
}

const arrowStyle = (enabled: boolean) => ({
  width: 32,
  height: 32,
  borderRadius: 32,
  border: `1px solid ${colors.greyLight}`,
  background: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: enabled ? "pointer" : "default",
  opacity: enabled ? 1 : 0.4,
  flexShrink: 0,
});

const Stepper: FC<StepperProps> = ({
  canDecrease,
  canIncrease,
  onDecrease,
  onIncrease,
  children,
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 16,
      marginTop: 8,
      marginInline: 16,
    }}
  >
    <button
      type="button"
      onClick={onDecrease}
      disabled={!canDecrease}
      style={arrowStyle(canDecrease)}
      aria-label="Decrease"
    >
      <Minus size={20} color={canDecrease ? colors.primary : colors.greyLight} />
    </button>
    <div style={{ flex: 1 }}>{children}</div>
    <button
      type="button"
      onClick={onIncrease}
      disabled={!canIncrease}
      style={arrowStyle(canIncrease)}
      aria-label="Increase"
    >
      <Plus size={20} color={canIncrease ? colors.primary : colors.greyLight} />
    </button>
  </div>
);

export default Stepper;
